/**
 * InvitationViewer — 공개 청첩장 뷰어 페이지
 *
 * /inv/:slugOrId 경로로 게스트가 접근.
 * 발행된 청첩장을 mode="viewer"로 렌더링.
 * 비밀번호 보호 시 잠금 화면 표시.
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router'
import type { Invitation, InvitationData, TemplateConfig } from '@cugumi/shared/schemas'
import { DEFAULT_TEMPLATE_CONFIG } from '@cugumi/shared/schemas'
import type { FlatInvitation } from '../stores/invitationStore.js'
import { getTemplateComponent } from '../components/templates/index.js'
import { apiFetch, getErrorMessage } from '../lib/api.js'
import { initKakao, shareKakao } from '../lib/kakao.js'
import { Button, Input, Spinner, Text, Container } from '../components/ui/index.js'
import styles from './InvitationViewer.module.css'

// ============================================================
// Types
// ============================================================

type ViewerState = 'loading' | 'locked' | 'viewing' | 'not_found' | 'expired' | 'draft'

interface LockedMeta {
  id: string
  locked: true
  groomName: string
  brideName: string
  coverImage?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
}

interface PublicInvitationResponse extends Invitation {
  templateConfig?: TemplateConfig
}

// ============================================================
// Cookie helpers
// ============================================================

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : null
}

function setCookie(name: string, value: string, maxAge: number): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`
}

// ============================================================
// Component
// ============================================================

export function InvitationViewer() {
  const { slugOrId } = useParams<{ slugOrId: string }>()

  const [state, setState] = useState<ViewerState>('loading')
  const [invitation, setInvitation] = useState<FlatInvitation | null>(null)
  const [templateConfig, setTemplateConfig] = useState<TemplateConfig>(DEFAULT_TEMPLATE_CONFIG)
  const [lockedMeta, setLockedMeta] = useState<LockedMeta | null>(null)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [verifying, setVerifying] = useState(false)

  const fetchInvitation = useCallback(async (token?: string) => {
    if (!slugOrId) return

    const headers: Record<string, string> = {}
    if (token) {
      headers['x-verification-token'] = token
    }

    const res = await apiFetch<PublicInvitationResponse | LockedMeta>(
      `/api/invitations/${slugOrId}/public`,
      { skipAuth: true, headers },
    )

    if (!res.success) {
      const errMsg = typeof res.error === 'string' ? res.error : res.error?.message || ''
      if (errMsg.includes('찾을 수 없')) setState('not_found')
      else if (errMsg.includes('만료') || errMsg.includes('삭제')) setState('expired')
      else if (errMsg.includes('공개되지 않')) setState('draft')
      else setState('not_found')
      return
    }

    const data = res.data as any

    if (data.locked) {
      setLockedMeta(data as LockedMeta)
      setState('locked')
      return
    }

    // 전체 데이터 — FlatInvitation 변환
    const invData = data as PublicInvitationResponse
    const flat: FlatInvitation = {
      ...invData,
      ...(invData.data as InvitationData),
    } as FlatInvitation

    setInvitation(flat)
    if (invData.templateConfig) {
      setTemplateConfig(invData.templateConfig)
    }
    setState('viewing')
  }, [slugOrId])

  // 초기 로드
  useEffect(() => {
    if (!slugOrId) return
    // 쿠키에서 기존 verification 토큰 확인
    const cookieToken = getCookie(`invitation_${slugOrId}_verified`)
    fetchInvitation(cookieToken || undefined)
  }, [slugOrId, fetchInvitation])

  // 카카오 SDK 초기화
  useEffect(() => { initKakao() }, [])

  // 비밀번호 제출
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!lockedMeta || !password.trim()) return

    setVerifying(true)
    setPasswordError('')

    const res = await apiFetch<{
      token: string
      cookieName: string
      cookieMaxAge: number
    }>(`/api/invitations/${lockedMeta.id}/verify`, {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({ password }),
    })

    setVerifying(false)

    if (!res.success) {
      setPasswordError(getErrorMessage(res.error, '비밀번호가 올바르지 않습니다'))
      return
    }

    // 쿠키 저장 + 재조회
    setCookie(res.data.cookieName, res.data.token, res.data.cookieMaxAge)
    // slugOrId 기반 쿠키도 저장 (slug 접근 시 사용)
    if (slugOrId !== lockedMeta.id) {
      setCookie(`invitation_${slugOrId}_verified`, res.data.token, res.data.cookieMaxAge)
    }
    setState('loading')
    fetchInvitation(res.data.token)
  }

  // 공유
  const handleShare = useCallback(async () => {
    if (!invitation) return
    const url = window.location.href
    const title = invitation.share?.ogTitle || `${invitation.groom?.name} ♥ ${invitation.bride?.name}`
    const description = invitation.share?.ogDescription || '결혼식에 초대합니다'

    // 네이티브 공유 지원 시
    if (navigator.share) {
      try {
        await navigator.share({ title, text: description, url })
        return
      } catch {
        // 사용자가 취소했거나 미지원 — fallback
      }
    }

    // 카카오톡 공유
    shareKakao({
      title,
      description,
      imageUrl: invitation.share?.ogImage || invitation.gallery?.coverImage,
      url,
    })
  }, [invitation])

  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
    } catch {
      // fallback
    }
  }, [])

  // ── 렌더링 ──────────────────────────────────────────────

  if (state === 'loading') {
    return (
      <div className={styles.centerScreen}>
        <Spinner size="lg" />
      </div>
    )
  }

  if (state === 'not_found') {
    return (
      <div className={styles.centerScreen}>
        <div className={styles.errorContent}>
          <Text variant="heading-lg">청첩장을 찾을 수 없습니다</Text>
          <Text variant="body-md" className={styles.errorSub}>
            주소를 다시 확인해주세요
          </Text>
        </div>
      </div>
    )
  }

  if (state === 'expired') {
    return (
      <div className={styles.centerScreen}>
        <div className={styles.errorContent}>
          <Text variant="heading-lg">만료된 청첩장입니다</Text>
          <Text variant="body-md" className={styles.errorSub}>
            이 청첩장은 더 이상 열람할 수 없습니다
          </Text>
        </div>
      </div>
    )
  }

  if (state === 'draft') {
    return (
      <div className={styles.centerScreen}>
        <div className={styles.errorContent}>
          <Text variant="heading-lg">아직 공개되지 않은 청첩장입니다</Text>
          <Text variant="body-md" className={styles.errorSub}>
            청첩장 주인이 발행하면 볼 수 있습니다
          </Text>
        </div>
      </div>
    )
  }

  if (state === 'locked' && lockedMeta) {
    return (
      <div className={styles.lockedScreen}>
        {lockedMeta.coverImage && (
          <div
            className={styles.lockedBg}
            style={{ backgroundImage: `url(${lockedMeta.coverImage})` }}
          />
        )}
        <div className={styles.lockedCard}>
          <Text variant="heading-md" className={styles.lockedNames}>
            {lockedMeta.groomName} & {lockedMeta.brideName}
          </Text>
          <Text variant="body-sm" className={styles.lockedHint}>
            비밀번호를 입력해주세요
          </Text>
          <form onSubmit={handlePasswordSubmit} className={styles.lockedForm}>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호"
              size="lg"
              autoFocus
            />
            {passwordError && (
              <Text variant="body-sm" className={styles.passwordError}>
                {passwordError}
              </Text>
            )}
            <Button type="submit" size="lg" loading={verifying} style={{ width: '100%' }}>
              확인
            </Button>
          </form>
        </div>
      </div>
    )
  }

  if (state === 'viewing' && invitation) {
    const TemplateComponent = getTemplateComponent(templateConfig.componentId)

    return (
      <div className={styles.viewerRoot}>
        <TemplateComponent
          invitation={invitation}
          config={templateConfig}
          mode="viewer"
        />
        <div className={styles.shareBar}>
          <button type="button" className={styles.shareBtn} onClick={handleShare}>
            공유하기
          </button>
          <button type="button" className={styles.shareBtn} onClick={handleCopyUrl}>
            URL 복사
          </button>
        </div>
      </div>
    )
  }

  return null
}
